<template>
  <FormDrawer
    ref="formDrawerRef"
    :visible="formDrawerState.visible"
    :form-id="formDrawerState.formId"
    :form-version="formVersion"
    :entity-type="formDrawerState.entityType"
    :entity-id="formDrawerState.entityId"
    :is-blocking="formDrawerState.isBlocking"
    :is-edit-mode="formDrawerState.isEditMode"
    :submission-id="formDrawerState.submissionId"
    :initial-values="formDrawerState.initialValues"
    @close="handleClose"
    @submit="handleSubmit"
    @submit-error="handleSubmitError"
  />
</template>

<script setup>
import { ref, watch } from "vue";
import { message } from "ant-design-vue";
import FormDrawer from "@/src/components/form_components/common/FormDrawer.vue";
import { useFormTrigger } from "@/src/composables/forms/useFormTrigger.js";
import { transport, BASE_PATH } from "@/src/config/apiConfig.js";
import ProcessFlowService from "@/src/api/ProcessFlowService.js";
import FormService from "@/src/api/FormService.js";
import eventBus from "@/src/utils/eventBus.js";

const {
  formDrawerState,
  executionFlowState,
  hideFormDrawer,
  handleFormSubmitSuccess,
  handleFormCancel,
} = useFormTrigger();

const formVersion = ref(null);
const formDrawerRef = ref(null);
const loading = ref(false);
const isSubmitSuccessful = ref(false);
const isSubmitting = ref(false);

watch(
  () => formDrawerState.value.triggerId,
  async (newVal) => {
    if (
      newVal &&
      formDrawerState.value.visible &&
      formDrawerState.value.formId
    ) {
      isSubmitSuccessful.value = false;
      isSubmitting.value = false;
      if (formDrawerRef.value) {
        formDrawerRef.value.resetSubmitting();
      }
      if (!formDrawerState.value.formVersion) {
        await fetchFormVersion();
      } else {
        formVersion.value = formDrawerState.value.formVersion;
      }
    }
  },
  { immediate: true },
);

const fetchFormVersion = async () => {
  try {
    loading.value = true;

    // Fetch BOTH: current form details (for name) AND published version (for schema)
    const [formDetailsResponse, versionResponse] = await Promise.all([
      FormService.forms.fetchFormDetails(formDrawerState.value.formId),
      FormService.versions.getPublishedVersion(formDrawerState.value.formId),
    ]);

    // Early return if version response is invalid
    if (!versionResponse?.success || !versionResponse?.version) {
      message.error("Failed to load form");
      hideFormDrawer();
      return;
    }

    const version = versionResponse.version;

    // Override the schema name with the current form name
    if (formDetailsResponse?.success && formDetailsResponse?.form?.name && version.schema_json) {
      version.schema_json = {
        ...version.schema_json,
        name: formDetailsResponse.form.name,
      };
    }

    formVersion.value = version;
  } catch (error) {
    console.error("Error fetching form version:", error);
    message.error("Failed to load form");
    hideFormDrawer();
  } finally {
    loading.value = false;
  }
};

const handleClose = () => {
  const isBlocking = formDrawerState.value.isBlocking;
  const isMultiExecutionFlow = executionFlowState.value.active;

  if (isBlocking && !isSubmitSuccessful.value && !isSubmitting.value) {
    if (isMultiExecutionFlow) {
      handleFormCancel();
      return;
    }
    const callbacks = formDrawerState.value.callbacks;
    if (callbacks?.onCancel) {
      callbacks.onCancel();
    }
  }

  if (formDrawerRef.value) {
    formDrawerRef.value.resetSubmitting();
  }

  formVersion.value = null;
  isSubmitSuccessful.value = false;
  isSubmitting.value = false;
  hideFormDrawer();
};

const handleSubmit = async (submissionData) => {
  if (isSubmitting.value) return;

  isSubmitting.value = true;

  try {
    if (submissionData.isEdit && submissionData.submissionId) {
      const updateResponse = await FormService.settings.updateSubmission(
        submissionData.submissionId,
        submissionData.values,
      );

      if (!formDrawerState.value.visible) {
        return;
      }

      if (!updateResponse?.success) {
        message.error("Failed to update form");
        if (formDrawerRef.value) {
          formDrawerRef.value.resetSubmitting();
        }
        isSubmitting.value = false;
        return;
      }

      const submission = updateResponse.submission || {};

      // Emit activity-logged so the activity list refreshes with updated data
      eventBus.emit("activity-logged");

      try {
        const formId = submission.form_id || submission.formId || submissionData.formId;
        const resubmitResponse = await ProcessFlowService.resubmitForm(
          String(formId),
          submission.entity_type || submission.entityType || submissionData.entityType,
          submission.entity_id || submission.entityId || submissionData.entityId,
          submission.values || submissionData.values,
        );

        isSubmitSuccessful.value = true;
        eventBus.emit("form-submitted");

        if (resubmitResponse.status === "paused" && resubmitResponse.form) {
          const originalCallback = formDrawerState.value.callbacks?.onSubmitSuccess;
          if (originalCallback) {
            try {
              originalCallback();
            } catch (callbackError) {
              console.error("Error in submission callback:", callbackError);
            }
          }

          executionFlowState.value.entityType = submissionData.entityType || formDrawerState.value.entityType;
          executionFlowState.value.entityId = submissionData.entityId || formDrawerState.value.entityId;

          executionFlowState.value.callbacks = {
            onAllComplete: () => {
              message.success("Form updated successfully");
            },
            onCancel: formDrawerState.value.callbacks?.onCancel,
          };
          handleFormSubmitSuccess(submissionData.values, resubmitResponse);
        } else {
          message.success("Form updated successfully");
          const callbacks = formDrawerState.value.callbacks;
          if (callbacks?.onSubmitSuccess) {
            try {
              await callbacks.onSubmitSuccess(updateResponse, {
                formVersion: formVersion.value,
                submittedValues: submissionData.values,
              });
            } catch (callbackError) {
              console.error("Error in submission callback:", callbackError);
            }
          }
          handleClose();
        }
      } catch (resubmitError) {
        console.error("Error resubmitting form:", resubmitError);
        isSubmitSuccessful.value = true;
        eventBus.emit("form-submitted");
        message.success("Form updated successfully");
        const callbacks = formDrawerState.value.callbacks;
        if (callbacks?.onSubmitSuccess) {
          callbacks.onSubmitSuccess(updateResponse, {
            formVersion: formVersion.value,
            submittedValues: submissionData.values,
          });
        }
        handleClose();
      }
    } else {
      const executionId = formDrawerState.value.executionId;
      const isMultiExecutionFlow =
        executionFlowState.value.active && executionId;

      const payload = {
        entity_type: submissionData.entityType,
        entity_id: submissionData.entityId,
        values: submissionData.values,
      };

      if (formDrawerState.value.objectId) {
        payload.object_id = formDrawerState.value.objectId;
      }

      if (formDrawerState.value.processFlowId) {
        payload.process_flow_id = formDrawerState.value.processFlowId;
      }

      const formsResponse = await transport.post(
        `${BASE_PATH}/api/forms/v1/${submissionData.formId}/submissions`,
        payload,
      );

      if (!formDrawerState.value.visible) {
        return;
      }

      if (!formsResponse.data?.success) {
        message.error(formsResponse.data?.message || "Failed to submit form");
        if (formDrawerRef.value) {
          formDrawerRef.value.resetSubmitting();
        }
        isSubmitting.value = false;
        return;
      }

      let processFlowResponse = { status: "completed", form: null };

      if (isMultiExecutionFlow) {
        try {
          processFlowResponse = await ProcessFlowService.submitExecution(
            executionId,
            submissionData.values,
          );

          if (!processFlowResponse.success) {
            message.error("Failed to continue process flow");
            if (formDrawerRef.value) {
              formDrawerRef.value.resetSubmitting();
            }
            isSubmitting.value = false;
            return;
          }
        } catch (error) {
          console.error("Error submitting to process flow:", error);
          message.error("Failed to continue process flow");
          if (formDrawerRef.value) {
            formDrawerRef.value.resetSubmitting();
          }
          isSubmitting.value = false;
          return;
        }
      }

      isSubmitSuccessful.value = true;
      eventBus.emit("form-submitted");
      message.success("Form submitted successfully");

      if (isMultiExecutionFlow || executionFlowState.value.active) {
        handleFormSubmitSuccess(submissionData.values, processFlowResponse);
      } else {
        const callbacks = formDrawerState.value.callbacks;
        const isBlocking = formDrawerState.value.isBlocking;

        if (callbacks?.onSubmitSuccess) {
          try {
            if (isBlocking) {
              await callbacks.onSubmitSuccess(formsResponse.data, {
                formVersion: formVersion.value,
                submittedValues: submissionData.values,
              });
            } else {
              callbacks.onSubmitSuccess(formsResponse.data, {
                formVersion: formVersion.value,
                submittedValues: submissionData.values,
              });
            }
          } catch (callbackError) {
            console.error("Error in submission callback:", callbackError);
          }
        }

        handleClose();
      }
    }
  } catch (error) {
    console.error("Error submitting form:", error);
    const errMsg = error?.response?.data?.message || "Failed to submit form";
    message.error(errMsg);
    if (formDrawerRef.value) {
      formDrawerRef.value.resetSubmitting();
    }
    isSubmitting.value = false;
  }
};

const handleSubmitError = () => {};
</script>
